import { MapPin, Phone, MessageCircle, Clock, Navigation, Languages } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, LANGUAGES, waLink, telLink, WA } from "../config/site";

export const VisitStore = () => (
  <section
    id="visit"
    className="py-16 sm:py-24 lg:py-32 bg-charcoal-surface border-y border-white/10"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>Easy to Reach. Easy to Shop.</Eyebrow>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-cream text-balance">
          Come Visit Us.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-cream/55 leading-relaxed">
          Find us on Ashram Kitchen Road in Udayapura, just off Kanakapura Main
          Road — open every day from 8 AM to 10 PM.
        </p>
      </Reveal>

      <div className="mt-12 grid lg:grid-cols-2 gap-6 lg:gap-8">
        <Reveal>
          <div className="h-full bg-charcoal-card border border-white/10 rounded-3xl p-7 sm:p-9 flex flex-col">
            <ul className="space-y-6 flex-1">
              <li className="flex gap-4">
                <span className="w-11 h-11 rounded-2xl bg-gold/12 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <MapPin size={19} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
                    Store Address
                  </p>
                  <p
                    className="mt-1.5 text-cream/85 text-sm sm:text-base"
                    data-testid="store-address"
                  >
                    {SITE.addressLine1}
                  </p>
                  <p className="text-cream/50 text-sm">{SITE.addressLine2}</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-11 h-11 rounded-2xl bg-gold/12 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <Phone size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
                    Call / WhatsApp
                  </p>
                  <a
                    href={telLink}
                    className="mt-1.5 block text-cream/85 text-sm sm:text-base hover:text-gold transition-colors"
                    data-testid="store-phone"
                  >
                    {SITE.phoneDisplay}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-11 h-11 rounded-2xl bg-gold/12 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <Clock size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
                    Store Hours
                  </p>
                  <p
                    className="mt-1.5 text-cream/85 text-sm sm:text-base"
                    data-testid="store-hours"
                  >
                    {SITE.hours}
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-11 h-11 rounded-2xl bg-gold/12 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <Languages size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
                    Languages Spoken
                  </p>
                  <p
                    className="mt-1.5 text-cream/85 text-sm sm:text-base"
                    data-testid="store-languages"
                  >
                    {LANGUAGES.map((l) => l.name).join(" · ")}
                  </p>
                </div>
              </li>
            </ul>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={SITE.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-directions-button"
                className="inline-flex items-center gap-2 bg-gold text-charcoal text-sm font-semibold rounded-full px-6 py-3 hover:bg-gold-hover transition-all duration-300 hover:-translate-y-0.5"
              >
                <Navigation size={15} /> Get Directions
              </a>
              <a
                href={telLink}
                data-testid="visit-call-button"
                className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm rounded-full px-6 py-3 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <Phone size={15} /> Call Now
              </a>
              <a
                href={waLink(WA.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-whatsapp-button"
                className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm rounded-full px-6 py-3 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <MessageCircle size={15} /> WhatsApp Us
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            data-testid="store-map"
            className="relative h-full min-h-[360px] rounded-3xl border border-gold/30 bg-charcoal overflow-hidden img-frame"
          >
            <iframe
              title="Om Sri Sri Collection on Google Maps"
              src={SITE.mapsEmbedUrl}
              className="absolute inset-0 w-full h-full border-0 grayscale-[35%] contrast-[1.05]"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="store-map-iframe"
            />
            <a
              href={SITE.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-get-directions-button"
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-charcoal/90 backdrop-blur-md border border-gold/50 text-gold text-sm rounded-full px-5 py-2.5 hover:bg-gold hover:text-charcoal transition-all duration-300 shadow-lg"
            >
              <Navigation size={15} /> Open in Google Maps
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
